import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { Plus, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", phone: "", address: "" };

export default function Clients() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try { const { data } = await api.get("/clients"); setClients(data); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", form);
      toast.success("Client created");
      setShowNew(false); setForm(EMPTY); load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/clients/${id}`); toast.success("Client deleted"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="p-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-medium text-[var(--c-text)]">Clients</h2>
          <p className="text-xs text-[var(--c-muted)] mt-0.5">{clients.length} accounts</p>
        </div>
        <Button data-testid="new-client-btn" onClick={() => setShowNew(true)}
          className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
          <Plus size={14} className="mr-1" /> New Client
        </Button>
      </div>

      <div className="mt-4 border border-[var(--c-border)] rounded-sm bg-[var(--c-surface)] overflow-x-auto">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead>
            <tr className="border-b border-[var(--c-border)]">
              {["Client", "Email", "Phone", "Location", "Projects", ""].map((h, i) => (
                <th key={i} className="text-left px-4 py-3 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.id.slice(0, 8)}`} onClick={() => nav(`/clients/${c.id}`)}
                className="border-b border-[var(--c-border)]/60 last:border-0 hover:bg-[var(--c-border)]/25 cursor-pointer transition-colors duration-150">
                <td className="px-4 py-3 text-xs font-medium text-[var(--c-text)]">{c.name}</td>
                <td className="px-4 py-3 text-xs text-[var(--c-muted)]">{c.email || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-muted)]">{c.phone || "—"}</td>
                <td className="px-4 py-3 text-xs text-[var(--c-muted)]">{c.address || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-text)]">{c.project_count}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`delete-client-${c.id.slice(0, 8)}`} onClick={(e) => remove(e, c.id)}
                    className="p-1.5 text-[var(--c-muted)] hover:text-[var(--c-red)] transition-colors duration-150">
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {clients.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-[var(--c-muted)]" data-testid="clients-empty">No clients yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" data-testid="new-client-dialog">
          <DialogHeader><DialogTitle className="text-[var(--c-text)]">New Client</DialogTitle><DialogDescription className="sr-only">Create a new client account</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            {[["name", "Client name", true], ["email", "Email", false], ["phone", "Phone", false], ["address", "Location / Address", false]].map(([k, label, req]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">{label}</Label>
                <Input data-testid={`nc-${k}`} required={req} value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
              </div>
            ))}
            <Button data-testid="nc-submit" className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">Create Client</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
